/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, forwardRef, useImperativeHandle } from "react";

import { Dropdown, Checkbox, Popover, Divider, Space } from "antd";
import { CheckboxChangeEvent } from "antd/es/checkbox";
import type { TFunction } from "i18next";
import type { i18n as I18nInstance } from "i18next";

import { Filter, JPInputFilterProps } from "./JPInputFilter.model";
import { Button } from "@companion-core/shared/app/Components/Button";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { useJPInputFilter } from "@companion-core/shared/app/Components/Inputs/JPInputFilter/hooks/useJPInputFilter";
import {
  trackClickFilter,
  trackClickFiltersButton,
  trackClickGeographicFilterSelection,
  trackFiltersDisplayed,
} from "@companion-core/shared/app/tracking/filters";
import { lockScroll, unlockScroll } from "@companion-core/web/src/Utilities/scroll";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { JPInputAutocompleteFilter } from "./JPAutocompleteFilter/JPInputAutocompleteFilter";
import { useFiltersStore } from "@companion-core/shared/app/Store/filters";

import "@companion-core/web/src/Assets/Styles/scss/inputFilter.scss";
import "@companion-core/web/src/Assets/Styles/scss/button.scss";

type JPInputFilterAutocompletePanelProps = {
  testID: string;
  savedFilters?: Record<string, string>;
  autocompleteFilterLabel?: string;
  setFilters: (filters: Record<string, string>) => void;
  setAutocompleteFilterLabel: (label?: string) => void;
  onGeographicSelection?: (label: string) => void;
};

type JPInputFilterPopoverContentProps = {
  testID: string;
  savedFilters?: Record<string, string>;
  autocompleteFilterLabel?: string;
  filterOptions: Filter[] | null;
  resultCount: number;
  setFilters: (filters: Record<string, string>) => void;
  setAutocompleteFilterLabel: (label?: string) => void;
  setIsPopoverOpen: (open: boolean) => void;
  onCheckboxChange: (e: CheckboxChangeEvent, name: string, key: string) => void;
  t: TFunction;
  i18n: I18nInstance;
  withGeolocalisation?: boolean;
  onGeographicSelection?: (label: string) => void;
};

function JPInputFilterAutocompletePanel({
  testID,
  savedFilters,
  autocompleteFilterLabel,
  setFilters,
  setAutocompleteFilterLabel,
  onGeographicSelection,
}: JPInputFilterAutocompletePanelProps) {
  return (
    <div className="input-filter__dropdown input-filter__dropdown-autocomplete">
      <JPInputAutocompleteFilter
        testID={testID}
        addressType="locality,country,continent"
        initialValue={
          savedFilters?.geo_place_id
            ? { placeId: savedFilters.geo_place_id, placeLevel: savedFilters.geo_place_level ?? "" }
            : undefined
        }
        initialLabel={autocompleteFilterLabel}
        onValueChange={(value) => {
          const nextFilters = { ...(savedFilters ?? {}) };
          if (value?.geo_place_id) {
            nextFilters["geo_place_id"] = value["geo_place_id"];
          } else {
            delete nextFilters["geo_place_id"];
          }
          if (value?.geo_place_level) {
            nextFilters["geo_place_level"] = value["geo_place_level"];
          } else {
            delete nextFilters["geo_place_level"];
          }
          setFilters(nextFilters);
          setAutocompleteFilterLabel(value?.label);
          if (value?.geo_place_id && value?.label) {
            onGeographicSelection?.(value.label);
          }
        }}
      />
    </div>
  );
}

function JPInputFilterPopoverContent({
  testID,
  savedFilters,
  autocompleteFilterLabel,
  filterOptions,
  resultCount,
  setFilters,
  setAutocompleteFilterLabel,
  setIsPopoverOpen,
  onCheckboxChange,
  t,
  i18n,
  withGeolocalisation = false,
  onGeographicSelection,
}: JPInputFilterPopoverContentProps) {
  return (
    <div className="input-filter__popover-content" data-testid={`input-filter__popover-${testID}`}>
      <>
        {withGeolocalisation && (
          <div className="input-filter__popover-content-autocomplete">
            <JPInputAutocompleteFilter
              testID={testID}
              addressType="locality,country,continent"
              initialValue={
                savedFilters?.geo_place_id
                  ? {
                      placeId: savedFilters.geo_place_id,
                      placeLevel: savedFilters.geo_place_level ?? "",
                    }
                  : undefined
              }
              initialLabel={autocompleteFilterLabel}
              onValueChange={(value) => {
                const nextFilters = { ...(savedFilters ?? {}) };
                if (value?.geo_place_id) {
                  nextFilters["geo_place_id"] = value["geo_place_id"];
                } else {
                  delete nextFilters["geo_place_id"];
                }
                if (value?.geo_place_level) {
                  nextFilters["geo_place_level"] = value["geo_place_level"];
                } else {
                  delete nextFilters["geo_place_level"];
                }
                setFilters(nextFilters);
                setAutocompleteFilterLabel(value?.label);
                if (value?.geo_place_id && value?.label) {
                  onGeographicSelection?.(value.label);
                }
              }}
            />
            <Divider />
          </div>
        )}
        {filterOptions &&
          filterOptions.map((filter, index) => (
            <div key={filter.name} className="input-filter__popover-content-checkboxes">
              {index > 0 && <Divider />}
              <span
                className="body-text-3 margin-bottom-24"
                data-testid={`input-filter__filter-title-${filter.name}-${testID}`}>
                {t(`common.filters.name.${filter.name}`)}
              </span>
              {filter.options.map((option) => (
                <Checkbox
                  data-testid={`input-filter__checkbox-${filter.name}-${option.key}-${testID}`}
                  value={option.key}
                  checked={option.checked ?? false}
                  key={option.key}
                  onChange={(e) => onCheckboxChange(e, filter.name, option.key)}>
                  <span className="body-text-6">
                    {i18n.exists(`common.filters.${filter.name}.${option.key}`)
                      ? t(`common.filters.${filter.name}.${option.key}`)
                      : option.key}
                  </span>
                </Checkbox>
              ))}
            </div>
          ))}
      </>
      <Button
        classNames={["input-filter__popover-content-cta"]}
        label={`${t("common.filters.see-results")} (${resultCount})`}
        testID={`input-filter__popover-cta-${testID}`}
        onClick={() => {
          setIsPopoverOpen(false);
          unlockScroll();
        }}
      />
    </div>
  );
}

export const JPInputFilter = forwardRef(function InputFilter(
  {
    testID,
    className,
    size,
    filters,
    onValueChange,
    withGeolocalisation = false,
  }: JPInputFilterProps,
  ref
) {
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { filters: savedFilters, setFilters } = useFiltersStore();
  const {
    t,
    i18n,
    filterOptions,
    checkedOptions,
    isPopoverOpen,
    checkedOptionsCount,
    resultCount,
    setInitialValues,
    updateCheckedValues,
    hasCheckedOptions,
    resetFilter,
    resetAllFilters,
    setFilterOptions,
    setIsPopoverOpen,
    setAutocompleteFilterLabel,
    autocompleteFilterLabel,
  } = useJPInputFilter({ filters, onValueChange });

  useImperativeHandle(
    ref,
    () => ({
      resetInput() {
        resetAllFilters();
      },
    }),
    [filters]
  );

  const onChange = useCallback(
    (e: CheckboxChangeEvent, name: string, key: string) => {
      if (!filterOptions) return;
      const filtersToUpdate = [...filterOptions];
      const filterIndex = filtersToUpdate.findIndex((filter) => filter.name === name);
      const checkBoxIndex = filtersToUpdate[filterIndex].options.findIndex(
        (option) => option.key === key
      );
      filtersToUpdate[filterIndex].options[checkBoxIndex].checked = e.target.checked;
      updateCheckedValues(name, key, e.target.checked);
      setFilterOptions(filtersToUpdate);
      if (onValueChange) onValueChange(filtersToUpdate);
      if (e.target.checked) {
        trackClickFilter({
          filterCategoryClicked: name,
          filterItemClicked: key,
          enabledFilters: checkedOptions,
        });
      }
    },
    [filterOptions, checkedOptions]
  );

  useEffect(() => {
    setInitialValues();
  }, []);

  const DropdownItems = ({ filter, name }: { filter: Filter; name: string }) => (
    <div
      className="input-filter__dropdown"
      data-testid={`input-filter__dropdown-${filter.name}-${testID}`}>
      <ul className="input-filter__dropdown-list">
        {filter.options.map((option) => (
          <li key={option.key}>
            <Checkbox
              data-testid={`input-filter__dropdown-checkbox-${filter.name}-${option.key}-${testID}`}
              value={option.key}
              checked={option.checked}
              onChange={(e) => onChange(e, filter.name, option.key)}>
              {i18n.exists(`common.filters.${filter.name}.${option.key}`)
                ? t(`common.filters.${filter.name}.${option.key}`)
                : option.key}
            </Checkbox>
          </li>
        ))}
      </ul>
      <Button
        label={t("common.filters.reset-all")}
        type="quaternary"
        testID={`input-filter__popover-${name}-reset-cta-${testID}`}
        onClick={() => resetFilter(name)}
      />
    </div>
  );

  const PopoverHeader = () => {
    return (
      <div className="input-filter__popover-header" data-testid={`input-filter__header-${testID}`}>
        <div className="input-filter__popover-header-section">
          <div
            data-testid={`input-filter__header-close-${testID}`}
            className="input-filter__popover-header-close"
            onClick={() => {
              setIsPopoverOpen(false);
              unlockScroll();
            }}>
            <FontIcon name="JP-Close-bold" size={24} />
          </div>
        </div>
        <span
          className="body-text-2 input-filter__popover-header-section input-filter__popover-header-title"
          data-testid={`input-filter__header-title-${testID}`}>
          {t("common.filters.button-label")}
        </span>
        <div className="body-text-7 input-filter__popover-header-section input-filter__popover-header-delete">
          <span
            data-testid={`input-filter__header-reset-${testID}`}
            className="input-filter__popover-header-delete-text"
            onClick={() => resetAllFilters()}>
            {t("common.filters.reset")}
          </span>
        </div>
      </div>
    );
  };

  const MobileLabel = () => {
    return (
      <span
        className="input-filter__mobile-label"
        data-testid={`input-filter__mobile-label-${testID}`}>
        {checkedOptionsCount > 0 ? checkedOptionsCount : t("common.filters.button-label")}
      </span>
    );
  };

  const DesktopLabel = ({ name }: { name: string }) => {
    const filter = checkedOptions.find((filter) => filter.name === name);
    const translatedLabel = i18n.exists(`common.filters.${name}.${filter?.checked[0]}`)
      ? t(`common.filters.${name}.${filter?.checked[0]}`)
      : filter?.checked[0];
    const label =
      filter && filter.checked.length > 0
        ? `${translatedLabel}${filter.checked.length > 1 ? ` +${filter.checked.length - 1}` : ""}`
        : t(`common.filters.name.${name}`);
    return (
      <>
        <span
          className="input-filter__desktop-label"
          data-testid={`input-filter__desktop-label-${name}-${testID}`}>
          {label}
        </span>
        {filter && filter.checked.length > 0 ? (
          <span
            className="input-filter__desktop-label-reset"
            data-testid={`input-filter__desktop-label-reset-${name}-${testID}`}
            onClick={(e) => {
              resetFilter(name);
              e.stopPropagation();
            }}>
            <FontIcon name="JP-Close-bold" />
          </span>
        ) : (
          <FontIcon name="JP-Arrow-down-bold" />
        )}
      </>
    );
  };

  return (
    <div className={`${className ?? ""} input-filter`} data-testid={`input-filter-${testID}`}>
      {isMobileSize ? (
        <Popover
          title={<PopoverHeader />}
          content={
            <JPInputFilterPopoverContent
              testID={testID}
              savedFilters={savedFilters}
              autocompleteFilterLabel={autocompleteFilterLabel}
              filterOptions={filterOptions}
              resultCount={resultCount}
              setFilters={setFilters}
              setAutocompleteFilterLabel={setAutocompleteFilterLabel}
              setIsPopoverOpen={setIsPopoverOpen}
              onCheckboxChange={onChange}
              t={t}
              i18n={i18n}
              withGeolocalisation={withGeolocalisation}
              onGeographicSelection={trackClickGeographicFilterSelection}
            />
          }
          open={isPopoverOpen}
          classNames={{ root: "input-filter__popover" }}
          onOpenChange={() => {
            trackClickFiltersButton();
            trackFiltersDisplayed({ enabledFilters: checkedOptions });
          }}>
          <Button
            testID={`input-filter__cta-${testID}`}
            classNames={["input-filter__button"]}
            type={
              hasCheckedOptions(
                undefined,
                Boolean(savedFilters?.geo_place_id) || Boolean(savedFilters?.geo_place_level)
              )
                ? "primary"
                : "quaternary"
            }
            label={
              <>
                <FontIcon name="JP-Filter-operation" size={16} />
                <MobileLabel />
              </>
            }
            onClick={() => {
              setIsPopoverOpen(true);
              lockScroll();
            }}
          />
        </Popover>
      ) : (
        <div className="input-filter__filters-desktop">
          {filterOptions &&
            filterOptions.map((filter) => (
              <Dropdown
                key={filter.name}
                placement="bottomLeft"
                classNames={{ root: "input-filter__ant-dropdown" }}
                trigger={["click"]}
                popupRender={() => <DropdownItems filter={filter} name={filter.name} />}
                onOpenChange={(open) => {
                  if (!open) return;
                  trackClickFiltersButton();
                  trackFiltersDisplayed({
                    enabledFilters: checkedOptions,
                    context: !isMobileSize ? "web_desktop_category" : "default",
                  });
                }}>
                <Space className="input-filter__dropdown-container">
                  <Button
                    testID={`input-filter__cta-${filter.name}-${testID}`}
                    classNames={["input-filter__desktop-button"]}
                    label={<DesktopLabel name={filter.name} />}
                    type={hasCheckedOptions(filter.name) ? "primary" : "quaternary"}
                    size={size}
                  />
                </Space>
              </Dropdown>
            ))}
          {withGeolocalisation && (
            <>
              <Dropdown
                placement="bottomLeft"
                classNames={{ root: "input-filter__ant-dropdown" }}
                trigger={["click"]}
                popupRender={() => (
                  <JPInputFilterAutocompletePanel
                    testID={testID}
                    savedFilters={savedFilters}
                    autocompleteFilterLabel={autocompleteFilterLabel}
                    setFilters={setFilters}
                    setAutocompleteFilterLabel={setAutocompleteFilterLabel}
                    onGeographicSelection={trackClickGeographicFilterSelection}
                  />
                )}
                onOpenChange={(open) => {
                  if (!open) return;
                  trackClickFiltersButton();
                  trackFiltersDisplayed({
                    enabledFilters: checkedOptions,
                    context: !isMobileSize ? "web_desktop_localisation" : "default",
                  });
                }}>
                <Space className="input-filter__dropdown-container">
                  <Button
                    testID={`input-filter__cta-autocomplete-${testID}`}
                    classNames={["input-filter__desktop-button"]}
                    label={
                      <span className="input-filter__desktop-label-geolocalisation">
                        {autocompleteFilterLabel ? (
                          <span className="input-filter__desktop-label-geolocalisation-text">
                            {autocompleteFilterLabel}
                          </span>
                        ) : (
                          <span className="input-filter__desktop-label-geolocalisation-text">
                            {t("common.filters.name.autocomplete")}
                          </span>
                        )}
                        <FontIcon name="JP-Arrow-down-bold" />
                      </span>
                    }
                    type={autocompleteFilterLabel ? "primary" : "tertiary"}
                    size="default"
                  />
                </Space>
              </Dropdown>
            </>
          )}
        </div>
      )}
    </div>
  );
});
